import React, { useEffect, useMemo, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { api } from "../lib/api";
import { useBrands } from "../contexts/BrandContext";
import { useAuth } from "../contexts/AuthContext";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import HorizontalScrollContainer from "../components/HorizontalScrollContainer";
import ReassignVacancyDialog from "../components/ReassignVacancyDialog";
import StickyPageHeader from "../components/StickyPageHeader";
import { formatDateFr } from "../lib/dateFormat";
import {
  History, Search, RotateCcw, UserCheck, UserX, Inbox, CheckCircle2, Trash2,
} from "lucide-react";

/**
 * Historique des interventions vacantes.
 *
 * 2 onglets :
 * - Réaffectées (status=replaced)
 * - Aucune réaffectation (status=open)
 *
 * Filtres : période (du/au), département, site, bénéficiaire, collaborateur, recherche libre.
 */
export default function HistoriqueVacances() {
  const { user } = useAuth();
  const { brands, selectedBrandId } = useBrands();

  const [tab, setTab] = useState("open");
  const [open, setOpen] = useState([]);
  const [replaced, setReplaced] = useState([]);
  const [loading, setLoading] = useState(true);

  const [sites, setSites] = useState([]);
  const [beneficiaries, setBeneficiaries] = useState([]);
  const [collabs, setCollabs] = useState([]);

  // Filtres (appliqués au submit pour ne pas spammer l'API)
  const [filters, setFilters] = useState({
    start_date: "",
    end_date: "",
    brand_id: "",
    site_id: "",
    beneficiary_id: "",
    collaborateur_id: "",
    q: "",
  });

  // Dialog réaffectation
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedVacancy, setSelectedVacancy] = useState(null);

  // Nettoyage en masse — admin uniquement
  const [cleanupLoading, setCleanupLoading] = useState(false);
  // Suppression individuelle — admin uniquement
  const [deletingId, setDeletingId] = useState(null);

  const isManager = user && (user.role === "admin" || user.role === "manager");

  const loadLists = async () => {
    try {
      const params = selectedBrandId ? { brand_id: selectedBrandId } : {};
      const [s, b, c] = await Promise.all([
        api.get("/sites", { params }).catch(() => ({ data: [] })),
        api.get("/beneficiaries", { params }).catch(() => ({ data: [] })),
        api.get("/collaborateurs", { params }).catch(() => ({ data: [] })),
      ]);
      setSites(s.data || []);
      setBeneficiaries(b.data || []);
      setCollabs(c.data || []);
    } catch {
      // noop
    }
  };

  const buildParams = (status) => {
    const params = { status };
    Object.entries(filters).forEach(([k, v]) => {
      if (v) params[k] = v;
    });
    // Le sélecteur d'en-tête force le filtre brand_id (prioritaire sur le filtre local)
    if (selectedBrandId) params.brand_id = selectedBrandId;
    return params;
  };

  const load = async () => {
    setLoading(true);
    try {
      const [o, r] = await Promise.all([
        api.get("/vacancies", { params: buildParams("open") }),
        api.get("/vacancies", { params: buildParams("replaced") }),
      ]);
      setOpen(o.data || []);
      setReplaced(r.data || []);
    } catch {
      setOpen([]);
      setReplaced([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadLists();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBrandId]);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBrandId]);

  const resetFilters = () => {
    setFilters({
      start_date: "", end_date: "", brand_id: "", site_id: "",
      beneficiary_id: "", collaborateur_id: "", q: "",
    });
  };

  const filteredSites = useMemo(() => {
    if (!filters.brand_id) return sites;
    return sites.filter((s) => (s.brand_ids || []).includes(filters.brand_id) || s.brand_id === filters.brand_id);
  }, [sites, filters.brand_id]);

  if (user && !isManager) return <Navigate to="/" replace />;

  const openReassign = (v) => {
    setSelectedVacancy({
      vacancy_id: v.id,
      date: v.date,
      start_time: v.start_time,
      end_time: v.end_time,
      brand_id: v.brand_id,
      brand_name: v.brand_name,
      site_name: v.site_name,
      poste: v.poste || v.event_title,
      origin: v.origin,
      absent_collaborateur_id: v.absent_collaborateur_id,
      absent_collaborateur_name: v.absent_collaborateur_name,
    });
    setDialogOpen(true);
  };

  // Nettoyage en masse — admin uniquement
  const isAdmin = user && user.role === "admin";

  const onReplaced = () => {
    load();
    window.dispatchEvent(new CustomEvent("plania:vacancies-updated"));
  };

  const cleanupExpired = async () => {
    if (!window.confirm(
      "Supprimer toutes les vacances ouvertes dont le délai est dépassé depuis plus de 7 jours ?\n\nCette action est irréversible."
    )) return;
    setCleanupLoading(true);
    try {
      const { data } = await api.post("/vacancies/cleanup-expired", null, { params: { days: 7 } });
      const n = data?.deleted ?? 0;
      const { toast } = await import("sonner");
      if (n > 0) toast.success(`${n} vacance(s) supprimée(s).`);
      else toast.info("Aucune vacance à nettoyer.");
      await load();
      window.dispatchEvent(new CustomEvent("plania:vacancies-updated"));
    } catch (e) {
      const { toast } = await import("sonner");
      toast.error(e.response?.data?.detail || "Erreur lors du nettoyage.");
    } finally {
      setCleanupLoading(false);
    }
  };

  const isVacancyElapsed = (v) => {
    if (!v || !v.date || !v.end_time) return false;
    try {
      // Compare au présent local (le fuseau utilisateur correspond généralement à Europe/Paris).
      const end = new Date(`${v.date}T${v.end_time}:00`);
      return !isNaN(end.getTime()) && end.getTime() <= Date.now();
    } catch {
      return false;
    }
  };

  const deleteVacancy = async (v) => {
    if (!window.confirm(
      `Supprimer définitivement cette vacance ?\n\n${formatDateFr(v.date)} • ${v.start_time}-${v.end_time}\n${v.poste || v.event_title || ""}\n\nCette action est irréversible.`
    )) return;
    setDeletingId(v.id);
    try {
      await api.delete(`/vacancies/${v.id}`);
      const { toast } = await import("sonner");
      toast.success("Vacance supprimée.");
      await load();
      window.dispatchEvent(new CustomEvent("plania:vacancies-updated"));
    } catch (e) {
      const { toast } = await import("sonner");
      toast.error(e.response?.data?.detail || "Erreur lors de la suppression.");
    } finally {
      setDeletingId(null);
    }
  };

  const renderTable = (rows, status) => {
    if (loading) {
      return <p className="p-6 text-sm text-zinc-500 font-mono">Chargement…</p>;
    }
    if (!rows || rows.length === 0) {
      return (
        <div className="p-10 text-center" data-testid={`vacancies-empty-${status}`}>
          {status === "replaced" ? (
            <CheckCircle2 size={28} className="mx-auto text-emerald-400 mb-2" />
          ) : (
            <Inbox size={28} className="mx-auto text-zinc-400 mb-2" />
          )}
          <p className="text-sm text-zinc-600">
            {status === "replaced"
              ? "Aucune vacance réaffectée pour ces filtres."
              : "Aucune vacance non-réaffectée pour ces filtres."}
          </p>
        </div>
      );
    }
    return (
      <HorizontalScrollContainer testId={`vacancies-${status}-scroll`} className="bg-white">
        <table className="w-full min-w-[1100px]" data-testid={`vacancies-table-${status}`}>
          <thead>
            <tr className="border-b border-zinc-200 bg-zinc-50">
              <th className="text-left label-uppercase p-3">Date</th>
              <th className="text-left label-uppercase p-3">Horaires</th>
              <th className="text-left label-uppercase p-3">Poste / Motif</th>
              <th className="text-left label-uppercase p-3">Département</th>
              <th className="text-left label-uppercase p-3">Site</th>
              <th className="text-left label-uppercase p-3">Absent</th>
              <th className="text-left label-uppercase p-3">
                {status === "replaced" ? "Remplaçant" : "Statut"}
              </th>
              {((status === "open" && isManager) || isAdmin) && (
                <th className="text-right label-uppercase p-3">Actions</th>
              )}
            </tr>
          </thead>
          <tbody>
            {rows.map((v) => (
              <tr
                key={v.id}
                className="border-b border-zinc-100 hover:bg-zinc-50/60"
                data-testid={`vacancy-row-${v.id}`}
              >
                <td className="p-3 font-mono text-xs text-zinc-600 whitespace-nowrap">
                  {formatDateFr(v.date)}
                </td>
                <td className="p-3 font-mono text-xs text-zinc-600 whitespace-nowrap">
                  {v.start_time} → {v.end_time}
                </td>
                <td className="p-3 text-sm text-zinc-900">
                  <p className="font-medium">{v.poste || v.event_title || "—"}</p>
                  <span
                    className={`inline-flex items-center px-1.5 py-0.5 rounded-sm border text-[10px] font-mono uppercase tracking-wider mt-1 ${
                      v.origin === "unavailability"
                        ? "bg-amber-50 text-amber-700 border-amber-200"
                        : "bg-red-50 text-red-700 border-red-200"
                    }`}
                  >
                    {v.origin === "unavailability" ? "Indisponibilité" : "Absence"}
                  </span>
                </td>
                <td className="p-3 text-sm text-zinc-700">{v.brand_name || "—"}</td>
                <td className="p-3 text-sm text-zinc-700">{v.site_name || "—"}</td>
                <td className="p-3 text-sm">
                  <Link
                    to={`/collaborateurs/${v.absent_collaborateur_id}/dossier`}
                    className="text-red-700 inline-flex items-center gap-1.5 hover:underline underline-offset-2"
                    data-testid={`vacancy-absent-link-${v.id}`}
                  >
                    <UserX size={12} />
                    {v.absent_collaborateur_name || "—"}
                  </Link>
                </td>
                <td className="p-3 text-sm">
                  {status === "replaced" ? (
                    <Link
                      to={`/collaborateurs/${v.replacement_collaborateur_id}/dossier`}
                      className="text-emerald-700 inline-flex items-center gap-1.5 hover:underline underline-offset-2"
                      data-testid={`vacancy-replacement-link-${v.id}`}
                    >
                      <UserCheck size={12} />
                      {v.replacement_collaborateur_name || "—"}
                    </Link>
                  ) : (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-sm bg-amber-50 text-amber-700 border border-amber-200 text-[11px] font-mono">
                      Ouverte
                    </span>
                  )}
                </td>
                {((status === "open" && isManager) || isAdmin) && (
                  <td className="p-3 text-right">
                    <div className="inline-flex items-center justify-end gap-1.5 flex-wrap">
                      {status === "open" && isManager && (
                        isVacancyElapsed(v) ? (
                          <span
                            className="inline-flex items-center px-2 py-0.5 rounded-sm bg-zinc-50 text-zinc-500 border border-zinc-200 text-[11px] font-mono"
                            data-testid={`vacancy-elapsed-${v.id}`}
                            title="L'heure de fin prévue est dépassée — réaffectation impossible"
                          >
                            Délai dépassé
                          </span>
                        ) : (
                          <button
                            type="button"
                            onClick={() => openReassign(v)}
                            className="inline-flex items-center gap-1.5 text-[11px] font-medium px-2.5 py-1.5 rounded-sm bg-zinc-900 text-white hover:bg-zinc-800"
                            data-testid={`vacancy-reassign-btn-${v.id}`}
                          >
                            <UserCheck size={11} />
                            Réaffecter
                          </button>
                        )
                      )}
                      {isAdmin && (
                        <button
                          type="button"
                          onClick={() => deleteVacancy(v)}
                          disabled={deletingId === v.id}
                          className="inline-flex items-center gap-1.5 text-[11px] font-medium px-2.5 py-1.5 rounded-sm bg-white text-red-700 border border-red-200 hover:bg-red-50 disabled:opacity-50"
                          data-testid={`vacancy-delete-btn-${v.id}`}
                          title="Supprimer définitivement cette vacance"
                        >
                          <Trash2 size={11} />
                          {deletingId === v.id ? "…" : "Supprimer"}
                        </button>
                      )}
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </HorizontalScrollContainer>
    );
  };

  return (
    <div className="space-y-6" data-testid="historique-vacances-page">
      <Tabs value={tab} onValueChange={setTab} className="w-full">
        <StickyPageHeader testid="vacances-sticky-header">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <p className="label-uppercase mb-2 inline-flex items-center gap-1.5">
            <History size={11} /> Historique
          </p>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-display font-bold tracking-tight text-zinc-900">
            Interventions vacantes
          </h1>
          <p className="text-sm text-zinc-600 mt-1 font-mono">
            Suivi des interventions impactées par une absence — réaffectées ou en attente.
          </p>
        </div>
        {isAdmin && (
          <Button
            type="button"
            variant="outline"
            onClick={cleanupExpired}
            disabled={cleanupLoading}
            className="border-red-200 text-red-700 hover:bg-red-50 hover:text-red-800"
            data-testid="vacancies-cleanup-expired-btn"
            title="Supprimer les vacances ouvertes dont le délai est dépassé depuis plus de 7 jours"
          >
            <Trash2 size={14} className="mr-1.5" />
            {cleanupLoading ? "Nettoyage…" : "Nettoyer les vacances expirées (> 7 j)"}
          </Button>
        )}
      </div>

      {/* Filtres */}
      <div className="bg-white border border-zinc-200 rounded-sm p-4 sm:p-5">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <div>
            <Label className="label-uppercase mb-1.5 block">Du</Label>
            <Input
              type="date"
              value={filters.start_date}
              onChange={(e) => setFilters({ ...filters, start_date: e.target.value })}
              data-testid="vac-filter-from"
            />
          </div>
          <div>
            <Label className="label-uppercase mb-1.5 block">Au</Label>
            <Input
              type="date"
              value={filters.end_date}
              onChange={(e) => setFilters({ ...filters, end_date: e.target.value })}
              data-testid="vac-filter-to"
            />
          </div>
          <div>
            <Label className="label-uppercase mb-1.5 block">Département</Label>
            <Select
              value={filters.brand_id || "_all"}
              onValueChange={(v) => setFilters({ ...filters, brand_id: v === "_all" ? "" : v, site_id: "" })}
            >
              <SelectTrigger data-testid="vac-filter-brand"><SelectValue placeholder="Tous" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="_all">Tous</SelectItem>
                {brands.map((b) => (
                  <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="label-uppercase mb-1.5 block">Site</Label>
            <Select
              value={filters.site_id || "_all"}
              onValueChange={(v) => setFilters({ ...filters, site_id: v === "_all" ? "" : v })}
            >
              <SelectTrigger data-testid="vac-filter-site"><SelectValue placeholder="Tous" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="_all">Tous</SelectItem>
                {filteredSites.map((s) => (
                  <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="label-uppercase mb-1.5 block">Bénéficiaire</Label>
            <Select
              value={filters.beneficiary_id || "_all"}
              onValueChange={(v) => setFilters({ ...filters, beneficiary_id: v === "_all" ? "" : v })}
            >
              <SelectTrigger data-testid="vac-filter-beneficiary"><SelectValue placeholder="Tous" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="_all">Tous</SelectItem>
                {beneficiaries.map((b) => (
                  <SelectItem key={b.id} value={b.id}>{b.denomination}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="label-uppercase mb-1.5 block">Collaborateur</Label>
            <Select
              value={filters.collaborateur_id || "_all"}
              onValueChange={(v) => setFilters({ ...filters, collaborateur_id: v === "_all" ? "" : v })}
            >
              <SelectTrigger data-testid="vac-filter-collab"><SelectValue placeholder="Tous" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="_all">Tous</SelectItem>
                {collabs.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="sm:col-span-2">
            <Label className="label-uppercase mb-1.5 block">Recherche libre</Label>
            <div className="relative">
              <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-400" />
              <Input
                type="text"
                placeholder="Titre, description, nom…"
                value={filters.q}
                onChange={(e) => setFilters({ ...filters, q: e.target.value })}
                onKeyDown={(e) => { if (e.key === "Enter") load(); }}
                className="pl-8"
                data-testid="vac-filter-search"
              />
            </div>
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 mt-4">
          <Button
            variant="outline"
            onClick={() => { resetFilters(); setTimeout(load, 0); }}
            data-testid="vac-filter-reset"
          >
            <RotateCcw size={13} className="mr-1.5" />
            Réinitialiser
          </Button>
          <Button
            onClick={load}
            className="bg-zinc-900 hover:bg-zinc-800 text-white"
            data-testid="vac-filter-apply"
          >
            <Search size={13} className="mr-1.5" />
            Appliquer
          </Button>
        </div>
      </div>

        <div className="w-full overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0">
          <TabsList className="w-max h-auto">
            <TabsTrigger value="open" data-testid="vac-tab-open">
              <Inbox size={14} className="mr-1.5" />
              Aucune réaffectation
              <span className="ml-1.5 px-1.5 py-0.5 rounded-sm bg-amber-50 text-amber-700 border border-amber-200 text-[10px] font-mono">
                {open.length}
              </span>
            </TabsTrigger>
            <TabsTrigger value="replaced" data-testid="vac-tab-replaced">
              <CheckCircle2 size={14} className="mr-1.5" />
              Réaffectées
              <span className="ml-1.5 px-1.5 py-0.5 rounded-sm bg-emerald-50 text-emerald-700 border border-emerald-200 text-[10px] font-mono">
                {replaced.length}
              </span>
            </TabsTrigger>
          </TabsList>
        </div>
        </StickyPageHeader>

        <TabsContent value="open" className="mt-3 bg-white border border-zinc-200 rounded-sm overflow-hidden">
          {renderTable(open, "open")}
        </TabsContent>
        <TabsContent value="replaced" className="mt-3 bg-white border border-zinc-200 rounded-sm overflow-hidden">
          {renderTable(replaced, "replaced")}
        </TabsContent>
      </Tabs>

      <ReassignVacancyDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        vacancy={selectedVacancy}
        onReplaced={onReplaced}
      />
    </div>
  );
}
